
import { useEffect, useState } from 'react';
import {
  motion,
  Variants,
  Transition,
  useAnimation,
  useReducedMotion,
} from 'framer-motion';
import { cn } from '@/lib/utils';

interface AnimatedLogoProps {
  size?: number;
  className?: string;
}

// ───── Layer 1: original stroke draw ─────
const pathVariants = {
  hidden: { pathLength: 0, opacity: 0 },
  visible: (i: number) => ({
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: {
        type: 'spring',
        duration: 1.5,
        bounce: 0,
        delay: i * 0.1,
      },
      opacity: { duration: 0.01, delay: i * 0.1 },
    },
  }),
};

// ───── Layer 2: original fill shine pulse ─────
const shineVariants: Variants = {
  animate: (i: number) => ({
    opacity: [0.6, 1, 0.6],
    transition: {
      duration: 2,
      delay: i * 0.2 + 0.5,
      ease: 'easeInOut',
    } as Transition,
  }),
};

// ───── Layer 3: specular sweep across the finished mark ─────
const SWEEP_DELAY = 2.4; // after fills mostly settle
const SWEEP_DURATION = 0.95;

const sweepPositionVariants: Variants = {
  hidden: { x: -120 },
  visible: {
    x: 280,
    transition: {
      duration: SWEEP_DURATION,
      delay: SWEEP_DELAY,
      ease: [0.65, 0.05, 0.36, 1],
    },
  },
};

const sweepOpacityVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: [0, 0.95, 0],
    transition: {
      duration: SWEEP_DURATION,
      delay: SWEEP_DELAY,
      times: [0, 0.5, 1],
      ease: 'easeInOut',
    } as Transition,
  },
};

// ───── Path data ─────
const PATHS = [
  // Red / Pink
  {
    d: 'M157.83,144.76c-2.96,2.01-5.4,3.45-7.55,4.61-19.01,10.26-41.75,13.36-63.37,10.97-21.47-2.28-42.82-8.68-61.33-18.72-5.04-2.81-9.74-5.84-14.45-9-2.1-1.15-10.02-6.45-10.02-6.45,0,0,0,30.92-.04,38.89-.01,1.96.29,2.97,1.97,4.26,2.79,2.03,6.29,4.01,9.44,5.74,31.65,16.96,81.15,32.57,116.82,18.52,19.45-7.91,30.75-26.64,28.89-45.64l-.37-3.17Z',
    gradientId: 'url(#gradient-red)',
  },
  // Green
  {
    d: 'M186.95,24.29C156,8.3,111.1-5.64,75.73,4.33c-16.75,4.74-29.31,18.48-33.55,33.48-1.07,3.77-1.96,8.11-1.74,12.33.09,1.8.82,6.9,1.05,6.78,0,0,4.08-3.15,5.62-4.23,4.19-2.94,8.82-5.42,13.64-7.25,24.57-8.92,53.91-6.79,79.01-.53,18.1,5.1,34.72,13.23,49.79,23.48,2.4,1.38,9.48,6.9,9.48,6.9,0,0-.07-3.72-.04-4.61.03-7.39.03-28.11,0-35.52.2-5.79-7.02-7.99-11.76-10.74l-.27-.13Z',
    gradientId: 'url(#gradient-green)',
  },
  // Orange / Yellow
  {
    d: 'M170.79,79.3c-18.57-14.29-42.8-20.74-66.45-23.99-10.61-1.27-23.09-2.94-32.26,3.09-8.4,5.35-10.6,16.71-4.29,24.08,11.37,12.71,33.72,8.33,49.35,13.39,23.03,5.81,42.76,22.57,47.11,44.11,3.45,16.04-.66,33.98-12.95,46.17-2.25,2.24-3.34,3.94-2.16,3.99,51.23-20.77,66.42-76.76,21.89-110.65l-.25-.19Z',
    gradientId: 'url(#gradient-orange)',
  },
  // Blue
  {
    d: 'M50.65,9.64s-18.31,6.88-28.56,16.95c-24.86,24.43-28.25,59.44-3.86,84.92,15.15,16.2,36.65,25.79,58.96,30.81,16.13,3.25,38.13,8.01,52.23-1.82,4.91-3.79,6.79-10.02,4.88-15.71l-.08-.24c-7.27-18.58-35.53-14.61-52.65-20.58-31.26-8.2-53.12-36.31-46.41-65.66,1.01-4.93,2.49-9.9,5.2-14.22',
    gradientId: 'url(#gradient-blue)',
  },
];

// Tints for the leading spark — matches the start of each path's gradient
const SPARK_TINTS = [
  'hsl(var(--brand-red))',
  'hsl(var(--brand-green))',
  'hsl(var(--brand-orange))',
  'hsl(var(--brand-blue-light))',
];

export function AnimatedLogo({ size = 48, className }: AnimatedLogoProps) {
  const controls = useAnimation();
  const [hasAnimated, setHasAnimated] = useState(false);
  // The mark's four moving layers — stroke draw, fill pulse, a 400px specular
  // sweep and a perpetual breath loop — are decoration on a logo that reads
  // perfectly well standing still. Under reduced motion we render the finished
  // frame: full fills, no sparks, no sweep, no breath.
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion || hasAnimated) return;
    controls.start('visible');
    setHasAnimated(true);
    controls.start('animate');
  }, [controls, hasAnimated, prefersReducedMotion]);

  return (
    // ───── Layer 4: ambient breath (very subtle scale loop, after settle) ─────
    <motion.div
      className={cn('inline-block origin-center will-change-transform', className)}
      style={{ width: size, height: size, lineHeight: 0 }}
      initial={{ scale: 1 }}
      animate={prefersReducedMotion ? { scale: 1 } : { scale: [1, 1.008, 1] }}
      transition={
        prefersReducedMotion
          ? { duration: 0 }
          : {
              duration: 4.6,
              delay: 3.6,
              repeat: Infinity,
              repeatType: 'loop',
              ease: 'easeInOut',
            }
      }
    >
      <motion.svg
        viewBox="0 0 200 200"
        width={size}
        height={size}
        initial={prefersReducedMotion ? 'visible' : 'hidden'}
        animate={prefersReducedMotion ? 'visible' : controls}
      >
        <defs>
          <linearGradient id="gradient-red" x1="1.07" y1="162.59" x2="158.4" y2="162.59" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="hsl(var(--brand-red))" />
            <stop offset="1" stopColor="hsl(var(--brand-dark-red))" />
          </linearGradient>
          <linearGradient id="gradient-green" x1="40.41" y1="38.13" x2="199.02" y2="38.13" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="hsl(var(--brand-green))" />
            <stop offset="1" stopColor="hsl(var(--brand-green-light))" />
          </linearGradient>
          <linearGradient id="gradient-orange" x1="64.15" y1="122.12" x2="197.33" y2="122.12" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="hsl(var(--brand-orange))" />
            <stop offset="1" stopColor="hsl(var(--brand-yellow))" />
          </linearGradient>
          <linearGradient id="gradient-blue" x1="1.59" y1="78.01" x2="135.03" y2="78.01" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="hsl(var(--brand-blue-light))" />
            <stop offset="1" stopColor="hsl(var(--brand-blue))" />
          </linearGradient>

          {/* Invisible path templates — referenced by spark <animateMotion> */}
          {PATHS.map((p, i) => (
            <path key={`tpl-${i}`} id={`logo-tpl-${i}`} d={p.d} fill="none" stroke="none" />
          ))}

          {/* Clip the specular sweep to the logo silhouette */}
          <clipPath id="logo-clip">
            {PATHS.map((p, i) => (
              <path key={`clip-${i}`} d={p.d} />
            ))}
          </clipPath>

          {/* Soft halo around the leading spark */}
          <filter id="spark-glow" x="-400%" y="-400%" width="900%" height="900%">
            <feGaussianBlur stdDeviation="2.4" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Specular sweep gradient — soft white band */}
          <linearGradient id="logo-sweep" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#ffffff" stopOpacity="0" />
            <stop offset="0.45" stopColor="#ffffff" stopOpacity="0.85" />
            <stop offset="0.55" stopColor="#ffffff" stopOpacity="0.85" />
            <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Strokes — draw in */}
        {PATHS.map((path, index) => (
          <motion.path
            key={`stroke-${index}`}
            d={path.d}
            variants={pathVariants}
            custom={index}
            stroke={path.gradientId}
            strokeWidth="3"
            strokeLinecap="round"
            fill="transparent"
          />
        ))}

        {/* Fills — pulse */}
        {PATHS.map((path, index) => (
          <motion.path
            key={`fill-${index}`}
            d={path.d}
            variants={shineVariants}
            custom={index}
            fill={path.gradientId}
          />
        ))}

        {/* ───── Layer 1: leading spark at the head of each stroke ─────
            SMIL (<animate>/<animateMotion>) ignores framer's reduced-motion
            handling entirely, so these are omitted from the tree instead. */}
        {!prefersReducedMotion && PATHS.map((_, i) => (
          <circle
            key={`spark-${i}`}
            r="2.6"
            fill={SPARK_TINTS[i]}
            opacity="0"
            filter="url(#spark-glow)"
          >
            <animate
              attributeName="opacity"
              values="0; 1; 1; 0"
              keyTimes="0; 0.06; 0.88; 1"
              dur="1.5s"
              begin={`${i * 0.1}s`}
              fill="freeze"
            />
            <animate
              attributeName="r"
              values="1.6; 2.8; 2.6; 1.6"
              keyTimes="0; 0.08; 0.85; 1"
              dur="1.5s"
              begin={`${i * 0.1}s`}
              fill="freeze"
            />
            <animateMotion
              dur="1.5s"
              begin={`${i * 0.1}s`}
              fill="freeze"
              rotate="auto"
            >
              <mpath href={`#logo-tpl-${i}`} />
            </animateMotion>
          </circle>
        ))}

        {/* Bright pinhole core (no glow filter) so the spark has a hot center */}
        {!prefersReducedMotion && PATHS.map((_, i) => (
          <circle
            key={`spark-core-${i}`}
            r="1.1"
            fill="#fff"
            opacity="0"
          >
            <animate
              attributeName="opacity"
              values="0; 1; 0.95; 0"
              keyTimes="0; 0.06; 0.88; 1"
              dur="1.5s"
              begin={`${i * 0.1}s`}
              fill="freeze"
            />
            <animateMotion
              dur="1.5s"
              begin={`${i * 0.1}s`}
              fill="freeze"
            >
              <mpath href={`#logo-tpl-${i}`} />
            </animateMotion>
          </circle>
        ))}

        {/* ───── Layer 3: specular sweep — clipped to the logo silhouette ─────
            A 400px horizontal travel; the single largest movement in the mark. */}
        {!prefersReducedMotion && (
          <g clipPath="url(#logo-clip)" style={{ mixBlendMode: 'overlay' }}>
            <motion.g variants={sweepOpacityVariants}>
              <motion.rect
                x="-100"
                y="-80"
                width="56"
                height="360"
                fill="url(#logo-sweep)"
                transform="rotate(22 0 100)"
                variants={sweepPositionVariants}
              />
            </motion.g>
          </g>
        )}
      </motion.svg>
    </motion.div>
  );
}
